import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RefreshCw, TrendingUp, Trophy, Brain,
  Target, BarChart3, Award, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const RANGE_OPTIONS = [
  { days: 7, label: '7 dias' },
  { days: 14, label: '14 dias' },
  { days: 30, label: '30 dias' },
];

const MetricCard = ({ label, value, color, hint }) => (
  <div className={`p-3 rounded-xl bg-slate-900/60 border ${color}`}>
    <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">{label}</div>
    <div className="text-white font-black text-2xl">{value}%</div>
    {hint && <div className="text-slate-500 text-[9px] mt-0.5">{hint}</div>}
  </div>
);

const DailyChart = ({ data, metric }) => {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d[metric] || 0), 100);
  const colorMap = {
    combo_rate: '#fbbf24',
    cuad25_rate: '#a855f7',
    cov42_rate: '#10b981',
    star_rate: '#f97316',
  };
  const color = colorMap[metric] || '#a855f7';

  return (
    <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800/50" data-testid={`perf-chart-${metric}`}>
      <div className="text-slate-300 text-xs font-bold mb-2">
        {metric === 'combo_rate' ? '⭐ Combo 3 Acierto Diario'
        : metric === 'cuad25_rate' ? '🎯 Cuadricula 25 Acierto Diario'
        : metric === 'cov42_rate' ? '🛡️ Cobertura 42 Acierto Diario'
        : '🌟 Numero Estrella Acierto Diario'}
      </div>
      <div className="h-48 flex items-end justify-between gap-1 px-1">
        {data.map((d) => {
          const h = max > 0 ? (d[metric] / max * 100) : 0;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d[metric]}%`}>
              <div className="text-[8px] text-slate-500">{d[metric] || 0}%</div>
              <div
                className="w-full rounded-t transition-all hover:opacity-80"
                style={{
                  height: `${Math.max(h, 2)}%`,
                  background: `linear-gradient(180deg, ${color} 0%, ${color}80 100%)`,
                  minHeight: '2px',
                }}
              />
              <div className="text-[7px] text-slate-600 rotate-45 origin-left whitespace-nowrap mt-1">
                {d.date.slice(5)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function TauroPerformancePage() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tauro/performance?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 30000,
      });
      setData(res.data);
    } catch (e) {
      toast.error('Error cargando performance');
    }
    setLoading(false);
  }, [token, days]);

  useEffect(() => { if (token) load(); }, [token, load]);

  if (!user) return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
      <p className="text-slate-400">Inicia sesion</p>
    </div>
  );

  const g = data?.global;
  const daily = data?.daily || [];
  const lotteries = data?.per_lottery || [];

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="tauro-performance-page">
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-6 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/tauro">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><ArrowLeft size={16} /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400 flex items-center gap-2">
                <TrendingUp className="text-emerald-400" size={24} /> Tauro Performance
              </h1>
              <p className="text-slate-500 text-sm">Aciertos reales del motor — auto-aprendizaje visible</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {RANGE_OPTIONS.map(opt => (
              <Button key={opt.days} variant={days === opt.days ? 'default' : 'outline'} size="sm"
                onClick={() => setDays(opt.days)}
                className={days === opt.days ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-slate-700 text-slate-400'}
                data-testid={`perf-range-${opt.days}`}>
                {opt.label}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
              className="border-emerald-700/40 text-emerald-400" data-testid="perf-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-20 gap-3">
            <Loader2 className="animate-spin text-emerald-400" size={36} />
            <p className="text-slate-500 text-sm">Calculando performance...</p>
          </div>
        ) : !data?.available ? (
          <div className="p-8 rounded-xl bg-slate-900/60 border border-amber-700/40 text-center">
            <AlertTriangle size={36} className="text-amber-400 mx-auto mb-3" />
            <h3 className="text-white font-bold mb-2">Sin datos suficientes aun</h3>
            <p className="text-slate-400 text-sm">{data?.message}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Hero metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Combo 3" value={g.combo_rate} color="border-amber-700/40" hint={`vs ~9% azar`} />
              <MetricCard label="Cuadricula 25" value={g.cuad25_rate} color="border-purple-700/40" hint={`vs ~25% azar`} />
              <MetricCard label="Cobertura 42" value={g.cov42_rate} color="border-emerald-700/40" hint={`vs ~42% azar`} />
              <MetricCard label="Estrella" value={g.star_rate} color="border-orange-700/40" hint={`vs ~3% azar`} />
            </div>

            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-950/40 to-cyan-950/30 border border-emerald-700/40">
              <div className="flex items-center gap-3 mb-2">
                <Brain className="text-emerald-400" size={20} />
                <div>
                  <h2 className="text-white font-bold">Track Record Real</h2>
                  <p className="text-slate-400 text-xs">{data.predictions_tracked} predicciones evaluadas en {data.days_covered} dias · {data.lotteries_covered} loterias</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm">
                Estos numeros NO son backtest — son los aciertos REALES de Tauro contra sorteos publicados. Cada dia este track record se vuelve mas preciso.
              </p>
            </div>

            {/* Daily charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DailyChart data={daily} metric="cuad25_rate" />
              <DailyChart data={daily} metric="cov42_rate" />
              <DailyChart data={daily} metric="combo_rate" />
              <DailyChart data={daily} metric="star_rate" />
            </div>

            {/* Ranking de loterías */}
            <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800/50" data-testid="perf-ranking">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={18} className="text-amber-400" />
                <h3 className="text-white font-bold">Ranking de Loterias mas Predecibles</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left border-b border-slate-800">
                      <th className="p-2 text-slate-500 font-bold">#</th>
                      <th className="p-2 text-slate-500 font-bold">Loteria</th>
                      <th className="p-2 text-center text-slate-500 font-bold">Tracked</th>
                      <th className="p-2 text-center text-amber-500 font-bold">Combo</th>
                      <th className="p-2 text-center text-purple-500 font-bold">Cuad 25</th>
                      <th className="p-2 text-center text-emerald-500 font-bold">Cob 42</th>
                      <th className="p-2 text-center text-orange-500 font-bold">Estrella</th>
                      <th className="p-2 text-center text-cyan-500 font-bold">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lotteries.map((l, i) => (
                      <tr key={l.lottery_id} className={`border-b border-slate-800/30 hover:bg-slate-800/30 ${
                        i === 0 ? 'bg-amber-950/20' : i === 1 ? 'bg-slate-800/30' : i === 2 ? 'bg-orange-950/20' : ''
                      }`} data-testid={`perf-lottery-${l.lottery_id}`}>
                        <td className="p-2 text-slate-400 font-bold">
                          {i === 0 && <Award size={14} className="text-amber-400 inline" />}
                          {i === 1 && <Award size={14} className="text-slate-300 inline" />}
                          {i === 2 && <Award size={14} className="text-orange-400 inline" />}
                          {i > 2 && (i + 1)}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-5 rounded-full" style={{ backgroundColor: l.color }} />
                            <span className="text-white font-bold">{l.lottery_name}</span>
                            <span className="text-slate-600 text-[10px]">{l.company}</span>
                          </div>
                        </td>
                        <td className="p-2 text-center text-slate-400">{l.tracked}</td>
                        <td className="p-2 text-center">
                          <span className={`font-bold ${l.combo_rate >= 10 ? 'text-amber-400' : 'text-slate-500'}`}>
                            {l.combo_rate}%
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className={`font-bold ${l.cuad25_rate >= 60 ? 'text-purple-400' : l.cuad25_rate >= 40 ? 'text-purple-500' : 'text-slate-500'}`}>
                            {l.cuad25_rate}%
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className={`font-bold ${l.cov42_rate >= 80 ? 'text-emerald-400' : l.cov42_rate >= 60 ? 'text-emerald-500' : 'text-slate-500'}`}>
                            {l.cov42_rate}%
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className={`font-bold ${l.star_rate >= 8 ? 'text-orange-400' : 'text-slate-500'}`}>
                            {l.star_rate}%
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className="text-cyan-300 font-black">{l.predictability_score.toFixed(2)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-slate-500 text-[10px] mt-3">
                <strong className="text-cyan-300">Score:</strong> ponderación de aciertos (combo×2 + cuad25×1.5 + cov42×1 + star×3). Las primeras 3 loterías son donde Tauro tiene mejor track record — recomendado concentrar inversión ahí.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30 text-xs text-slate-400">
              <strong className="text-emerald-400">Como crece esto?</strong> Cada vez que abres una lotería en Tauro, la predicción se guarda. Cuando el sorteo se publica, comparamos automáticamente y los resultados aparecen aquí. Mientras mas uses el sistema, mas precisos los datos.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
